import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';

@Component({
  selector: 'ca-quality-management-settings',
  templateUrl: './quality-management-settings.component.html',
  styleUrls: ['./quality-management-settings.component.scss'],
  standalone: false,
})
export class QualityManagementSettingsComponent implements OnInit {
  qualityManagementSettingsForm: FormGroup;
  qualityManagementSettingDtos: ConfigurationSettingsDto[] = [];
  private readonly settingKeyCalibrateWithFilledForm =
    'QualityManagement.Calibration.CalibrateWithFilledForm';
  private readonly settingKeyGroupAutomaticEvaluations =
    'QualityManagement.AutomaticEvaluation.GroupBy';
  private readonly settingKeyShowAutomaticEvaluationScore =
    'QualityManagement.AutomaticEvaluation.ShowAutomaticEvaluationScore';
  private readonly settingKeyAutomaticEvaluationObjectionEnabled =
    'QualityManagement.AutomaticEvaluation.AutomaticEvaluationObjectionEnabled';
  private readonly settingKeyObjectionLimit = 'QualityManagement.Evaluation.ObjectionLimit';
  private readonly settingKeyAllowMultipleEvaluation =
    'QualityManagement.Evaluation.AllowMultipleEvaluation';

  private readonly settingKeyNotificationHourly =
    'QualityManagement.EvaluationResult.Notification.Hourly';
  private readonly settingKeyNotificationDaily =
    'QualityManagement.EvaluationResult.Notification.Daily';
  private readonly settingKeyNotificationWeekly =
    'QualityManagement.EvaluationResult.Notification.Weekly';
  private readonly settingKeyNotificationMonthly =
    'QualityManagement.EvaluationResult.Notification.Monthly';

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {
    this.qualityManagementSettingsForm = fb.group({
      calibrationSettingsForm: fb.group({
        calibrateWithFilledForm: [
          JSON.parse(this.config.getSetting(this.settingKeyCalibrateWithFilledForm).toLowerCase()),
        ],
      }),
      groupAutomaticEvaluationsForm: fb.group({
        groupEvaluationsBy: [this.config.getSetting(this.settingKeyGroupAutomaticEvaluations)],
        showAutomaticEvaluationScore: [
          JSON.parse(
            this.config.getSetting(this.settingKeyShowAutomaticEvaluationScore).toLowerCase()
          ),
        ],
        automaticEvaluationObjectionEnabled: [
          JSON.parse(
            this.config.getSetting(this.settingKeyAutomaticEvaluationObjectionEnabled).toLowerCase()
          ),
        ],
      }),
      evaluationSettingsForm: fb.group({
        objectionLimit: [
          JSON.parse(this.config.getSetting(this.settingKeyObjectionLimit).toLowerCase()),
        ],
        allowMultipleEvaluation: [
          JSON.parse(this.config.getSetting(this.settingKeyAllowMultipleEvaluation).toLowerCase()),
        ],
      }),
      evaluationEmailAlertSettingsForm: fb.group({
        hourly: [
          JSON.parse(this.config.getSetting(this.settingKeyNotificationHourly).toLowerCase()),
        ],
        daily: [JSON.parse(this.config.getSetting(this.settingKeyNotificationDaily).toLowerCase())],
        weekly: [
          JSON.parse(this.config.getSetting(this.settingKeyNotificationWeekly).toLowerCase()),
        ],
        monthly: [
          JSON.parse(this.config.getSetting(this.settingKeyNotificationMonthly).toLowerCase()),
        ],
      }),
    });
  }

  ngOnInit(): void {}

  onSubmitQualityManagementSettings() {
    if (this.qualityManagementSettingsForm.invalid) {
      return;
    }
    this.qualityManagementSettingDtos = new Array();

    this.addCalibrationSettings();
    this.addObjectionLimitSettings();
    this.addAutomaticAssignmentSettings();
    this.addShowAutomaticAssignmentScoreSettings();
    this.addAutomaticEvaluationObjectionEnabledSettings();
    this.addNotificationHourlySettings();
    this.addNotificationDailySettings();
    this.addNotificationWeeklySettings();
    this.addNotificationMonthlySettings();
    this.addMultipleEvaluationSettings();

    this.settingService.saveSetting(this.qualityManagementSettingDtos).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }

  private addCalibrationSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyCalibrateWithFilledForm,
      settingValue: this.qualityManagementSettingsForm
        .get('calibrationSettingsForm.calibrateWithFilledForm')
        .value.toString(),
    });
  }

  private addObjectionLimitSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyObjectionLimit,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationSettingsForm.objectionLimit')
        .value.toString(),
    });
  }

  private addShowAutomaticAssignmentScoreSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyShowAutomaticEvaluationScore,
      settingValue: this.qualityManagementSettingsForm
        .get('groupAutomaticEvaluationsForm.showAutomaticEvaluationScore')
        .value.toString(),
    });
  }

  private addAutomaticEvaluationObjectionEnabledSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyAutomaticEvaluationObjectionEnabled,
      settingValue: this.qualityManagementSettingsForm
        .get('groupAutomaticEvaluationsForm.automaticEvaluationObjectionEnabled')
        .value.toString(),
    });
  }

  private addAutomaticAssignmentSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyGroupAutomaticEvaluations,
      settingValue: this.qualityManagementSettingsForm.get(
        'groupAutomaticEvaluationsForm.groupEvaluationsBy'
      ).value,
    });
  }

  private addNotificationHourlySettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyNotificationHourly,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationEmailAlertSettingsForm.hourly')
        .value.toString(),
    });
  }

  private addNotificationDailySettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyNotificationDaily,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationEmailAlertSettingsForm.daily')
        .value.toString(),
    });
  }

  private addNotificationWeeklySettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyNotificationWeekly,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationEmailAlertSettingsForm.weekly')
        .value.toString(),
    });
  }

  private addNotificationMonthlySettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyNotificationMonthly,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationEmailAlertSettingsForm.monthly')
        .value.toString(),
    });
  }

  private addMultipleEvaluationSettings() {
    this.qualityManagementSettingDtos.push({
      settingName: this.settingKeyAllowMultipleEvaluation,
      settingValue: this.qualityManagementSettingsForm
        .get('evaluationSettingsForm.allowMultipleEvaluation')
        .value.toString(),
    });
  }
}
